import { useEffect, useState } from "react";
import { TGameState, useGame } from "../../../data/gameContext";
import '../encounter.css';
import { TCharacterAttributes } from "../../../data/character";
import { GetRandomNumber, CapitaliseWords } from "../../../helper/common";
import { gameConfig } from "../../gameConfig";

type TCombat={
    Player:TCombatPlayer
    AI: TCombatAI
    Rounds: TCombatRound[];
    AtMeleeRange: boolean 
    PlayerWon: boolean
    PlayerLost: boolean
    CombatantFled: boolean //player doesn't gain or lose any inventory / money if one combatant flees
    //probably need to track the bullets used / 
}

type TCombatRound={
    PlayerAction: ECombatActions,
    AiAction: ECombatActions,    
    AtMeleeRange: boolean,
    Events: TCombatEvent[]
}

type TCombatEvent={
    Instigator: TCombatant
    Victim: TCombatant
    IsMelee: boolean
    AttackFraction: number //1 for a full attack, 0.5 for a half attack like a counter
    Success: boolean
    Damage: number //more for auditing than anything.
}

type TCombatant={
    IsPlayer: boolean
    Stamina: number
    Stats: TCharacterAttributes    
    IsHuman: boolean //players are never human.    
    BulletsLeftInGun: number 
    //need one for weapon once the player has an inventory
}

type TCombatAI=TCombatant & {
    Description: string //may give hints as to a good course of action 
    PredictedNextAction: boolean 
    Intelligence: number //0 = random, 1 = smart, 2 = dirty cheat
    //probably need to generate what sort of gun it has for ranged.
    //AI never runs out of bullets but does have to reload
}

type TCombatPlayer=TCombatant & {   
    FutureActions: ECombatActions[]
}

enum ECombatActions{
    Flee="flee",
    MeleeAttack="punch",
    MeleeCounter="block then punch",
    MeleeBlock="grapple from behind",
    RangedAttack="aim and fire",
    RangedCounter="shoot from the hip.",
    RangedBlock="dive for cover and return fire.",
    Reload="reload",
    Advance="dodge close to your opponent.",
    Retreat="dance away from your opponent."
}

export default function Encounter() {
    const { gameState, updateGameState } = useGame();

    const [combatState, setCombatState] = useState<TCombat>(initialCombatState(gameState));

    function initialCombatState(gameState: TGameState): TCombat {
        let newCombat:TCombat = {
            Rounds: [],
            AtMeleeRange: false, //always start at shooting range
            PlayerWon:false,
            PlayerLost: false,
            CombatantFled: false,
            Player: {
                IsPlayer: true,
                Stamina: 100,
                BulletsLeftInGun: 6, //need to check inventory and gun type
                Stats: gameState.PlayerCharacter!.Stats,
                IsHuman: false, 
                FutureActions: []},
            AI: generateCombatOpponent(gameState)
        };
        return newCombat
    }

    function generateCombatOpponent(gameState: TGameState): TCombatAI {
        let newOpponent: TCombatAI = {
            IsPlayer: false,
            Description: "tough looking bandit",
            Stamina: 100, 
            IsHuman: false,
            PredictedNextAction: false,
            BulletsLeftInGun: 6, //need to check the gun type the opponent has
            Intelligence: 0,
            Stats: {
                Reflexes: 100, 
                Speed: 100, 
                Strength: 100, 
                Toughness: 100, 
                Weight: 100}
        }
        return newOpponent;
    }
  

    function doAnAction(oldCombat: TCombat, action: ECombatActions): TCombat {
        //copy the old combat state so we can make a new one
        let newCombat = structuredClone(oldCombat);

        //determine the AI response - AI may have guessed players future actions
        let aiAction = getAIAction(oldCombat)

        //create the combat round object
        let newRound:TCombatRound= {PlayerAction: action, AiAction: aiAction, AtMeleeRange: newCombat.AtMeleeRange, Events: []}

        //work out which combatant gets to do what based on the two actions
        newRound.Events = resolveActions(newCombat, newRound);

        //resolve one or both events
        if(newRound.Events.length) resolveEvents(newRound);

        //determine whether either combatant has run out of stamina and loses
        checkCombatEnded(newCombat);

        //if combat is over need to determine, enact and display the consequences

        //if combat is not over advance player actions and recurse? 
        // Also need to check if AI can predict the next action, and reset that if there are no more actions. He should only get one chance to predict, not one per action. Maybe.

        //add the new round and set the combat state - don't add before in case we need to examine the prior ones
        newCombat.Rounds= [...newCombat.Rounds, newRound]
        console.log(newCombat);
        return newCombat;
    }


    function getAIAction(currentCombat: TCombat): ECombatActions {
        //check flee

        //check change range
        
        //check reload if shooting

        //choose action for range, based on model, start with RandomAI - chooses any appropriate action for the range, except reload or flee. 
        switch(GetRandomNumber(3)){            
        case 0:
            return combatState.AtMeleeRange ? ECombatActions.MeleeAttack : ECombatActions.RangedAttack
        case 1:
            return combatState.AtMeleeRange ? ECombatActions.MeleeCounter : ECombatActions.RangedCounter
        default:
            return combatState.AtMeleeRange ? ECombatActions.MeleeBlock : ECombatActions.RangedBlock
        }
    }

    function getPlayerOptions(currentCombat:TCombat): ECombatActions[] {
        //can onyl travel if the fight is resolved.
        if(currentCombat.CombatantFled || currentCombat.PlayerWon || currentCombat.PlayerLost) return [];

        let availableActions: ECombatActions[] = [];
        if( currentCombat.AtMeleeRange) {
            availableActions = [ECombatActions.MeleeAttack, ECombatActions.MeleeCounter, ECombatActions.MeleeBlock, ECombatActions.Retreat];
        }
        else
        {
            //check for reload / bullets
            availableActions = [ECombatActions.RangedAttack, ECombatActions.RangedCounter, ECombatActions.RangedBlock, ECombatActions.Advance];
        }
        //add flee (and any other non-ranged specific actions if we add them!)
        availableActions = [...availableActions,ECombatActions.Flee]
        return availableActions;
    }

    function resolveActions(newCombat: TCombat, newRound: TCombatRound): TCombatEvent[] {

        //determine who gets to do what based on their chosen actions
        let events: TCombatEvent[] = [];

        //flee - determine the results of any flee attempts

        //both combatants lose stamnina for actions, unless the player attempted a combo
        newCombat.Player.Stamina-=gameConfig.BaseActionCost;
        newCombat.AI.Stamina-=gameConfig.BaseActionCost;

        //determine who gets an attack based on the current distance and chosen actions - check neither is trying to change range first
        if(!([newRound.AiAction, newRound.PlayerAction].some(item => [ECombatActions.Advance, ECombatActions.Retreat].includes(item)))){
            //Player first then AI.
            let fraction = rockPaperScissors(newRound.PlayerAction, newRound.AiAction, newRound.AtMeleeRange);
            if(fraction !== 0)  events.push({Instigator: combatState.Player, Victim: combatState.AI, IsMelee: newRound.AtMeleeRange, AttackFraction: fraction, Success: false, Damage: 0});
         
            fraction = rockPaperScissors(newRound.AiAction, newRound.PlayerAction, newRound.AtMeleeRange);
            if(fraction !== 0)  events.push({Instigator: combatState.AI, Victim: combatState.Player, IsMelee: newRound.AtMeleeRange, AttackFraction: fraction, Success: false, Damage: 0});
        }
        else
        {
            //range changes - this supersedes normal opposed attacks
            if((newRound.AiAction === ECombatActions.Retreat && newRound.PlayerAction=== ECombatActions.Retreat ||
                newRound.AiAction === ECombatActions.Advance && newRound.PlayerAction=== ECombatActions.Advance)) {
                    newCombat.AtMeleeRange = !newCombat.AtMeleeRange //Both combatants want to change, change   
            }
            else if(newRound.AiAction === ECombatActions.Retreat || newRound.AiAction === ECombatActions.Advance) {    
                //AI changing range, determine if the attempt is successful, and if the opponent gets an attack                
                events.push({Instigator: combatState.Player, Victim: combatState.AI, IsMelee: newCombat.AtMeleeRange, AttackFraction: 1, Success: false, Damage: 0}) //just add the attack anyway for now
                newCombat.AtMeleeRange = !newCombat.AtMeleeRange

            } 
            else  { //Player changing range, determine if the attempt is successful, and if the opponent gets an attack
                events.push({Instigator: combatState.AI, Victim: combatState.Player, IsMelee: newCombat.AtMeleeRange, AttackFraction: 1, Success: false, Damage: 0}) //just add the attack anyway for now
                newCombat.AtMeleeRange = !newCombat.AtMeleeRange
            }
        }                

        //reload guns for any chosen reload actions
        return events;
    }

    function rockPaperScissors(action: ECombatActions, response: ECombatActions, atMeleeRange:boolean): number{
        //several ways we could write this, I'm trying to make this clear:
        
        //Return a -1 to indicate the attack is at the wrong range
        if([ECombatActions.MeleeAttack, ECombatActions.MeleeBlock, ECombatActions.MeleeCounter].includes(action) && !atMeleeRange) return -1
        if([ECombatActions.RangedAttack, ECombatActions.RangedBlock, ECombatActions.RangedCounter].includes(action) && atMeleeRange) return -1
        
        //if the victim is using an attack at the wrong range always get a full hit
        if([ECombatActions.MeleeAttack, ECombatActions.MeleeBlock, ECombatActions.MeleeCounter].includes(response) && !atMeleeRange) return 1
        if([ECombatActions.RangedAttack, ECombatActions.RangedBlock, ECombatActions.RangedCounter].includes(response) && atMeleeRange) return 1

        //Melee actions
        if(action === ECombatActions.MeleeAttack && response !== ECombatActions.MeleeBlock) return 1; //attack can be blocked
        if(action === ECombatActions.MeleeCounter) return 0.5; //counter always works, but only does half damage
        if(action === ECombatActions.MeleeBlock && response === ECombatActions.MeleeAttack) return 1; //block can only damage an attack

        //Ranged actions 
        if(action === ECombatActions.RangedAttack && response !== ECombatActions.RangedBlock) return 1; //attack can be blocked
        if(action === ECombatActions.RangedCounter) return 0.5; //counter always works, but only does half damage
        if(action === ECombatActions.RangedBlock && response === ECombatActions.RangedAttack) return 1; //block can only damage an attack

        //if the user was reloading or changing range or something do nothing
        return 0;

    }
    
    function resolveEvents(round: TCombatRound) {
        //Actually resolve what happens when one combatant gets to attack another
        round.Events.forEach(event => {
            //determine success - eventually needs to be an opposed roll using the stats of the combatants - just flip a coin for now.
            event.Success = Math.random() >= 0.5; //need the audit trail of whether each was successful
            if(event.Success) 
            {
                //determine damage - should be based on strength for melee, and weapon dmg for shooting - make it a flat 5 + d10 for now
                //Multiply by attack fraction
                
                event.Victim.Stamina-= Math.floor(event.AttackFraction * (6 + GetRandomNumber(10))); //randomNumber10 returns 0 to 9
            }            
        });
    }

    function checkCombatEnded(newCombat: TCombat) {
        //Player wins a draw, so check AI stam first
        if (newCombat.AI.Stamina <= 0) {
            newCombat.PlayerWon = true;
            return;
        }
        if (newCombat.Player.Stamina <= 0) {
            newCombat.PlayerLost = true;
            return;
        }
    }

    //text functions - replace with UX eventually - but don't show stats.
    function describeRound(round: TCombatRound):string {
        let desc = `You tried to ${round.PlayerAction} and they tried to ${round.AiAction}.` +
        round.Events.map(evt => (
             `${evt.Instigator.IsPlayer ? " You" : " They"} ${round.AtMeleeRange ? " threw a punch at" : " took a shot at "}${ evt.Victim.IsPlayer ? " you, " : " them, "} `
             +  `${evt.AttackFraction === -1 ? round.AtMeleeRange ? " but they knocked your gun aside as you were too close." : " but couldn't reach them." : 
                evt.Success ? "and hit!" : "but missed!"}` //resolve events properly for result. SHould probably describe strenght of hit somewhere
        ))
        return desc;
    }
        
    function getPlayerState(combatState: TCombat): string {
        //100- 90 Good, 89 - 70 OK, 69 - 40 strating to hurt, 39-20 in pain, >20 last legs
        if(combatState.Player.Stamina >= 90) return "You feel good";
        if(combatState.Player.Stamina >= 70) return "You feel OK";
        if(combatState.Player.Stamina >= 40) return "You're starting to feel tired and hurt";
        if(combatState.Player.Stamina >= 20) return "You're in real pain";
         return "You're on your last legs";
    }

    function getAIState(combatState: TCombat): string {
        //100- 90 Good, 89 - 70 OK, 69 - 40 strating to hurt, 39-20 in pain, >20 last legs
        if(combatState.AI.Stamina >= 70) return "looks confident";
        if(combatState.AI.Stamina >= 30) return "seems annoyed";
         return "is starting to stagger";
    }

    //combat encounter. 
    //PC can try and flee - based on relative speed, may get away if fast, but needs a big success. May take a parting shot but get away with small success. No effect on small fail. Enemy gets a hit and they don't get away on big fail.
    //user takes cosmetic damage from flee hits, and also fight damage from failed hits.


    //combat mechanics
    //determine if opponent is human - chance increases based on player cash
    //determine AI stats - total stats similar to player - less for easy, samish for medium, more for greater.
    //fights always start at shooting range (special circumstances maybe??)
    //show description of opponent, may hint at combat type, and relative power. May indicate how close to done he is. If human same description but means nothing, always wins, unless you flee, maybe get one chance to get away based on class. 
    //show description of your current state (100- 90 Good, 89 - 70 OK, 69 - 40 strating to hurt, 39-20 in pain, >20 last legs)
    //player chooses action. Actions cost stam, and so does being hit? Multiple actions can be queued for the same cost as one action.
    //AI will choose an action based partially on previous results. AI never tries to predict. AI may try to flee, but onyl when it will lose to any hit.
    //combat occurs, results displayed (repeat if multiple actions chosen)
    //determine victory conditions
    //if multiple actions chosen, run each one in turn, checkign victory loss each time.
    //if no winner user chooses more actions.


    //Actions:
    //close to melee - opponent may get a free shot if they choose a shooting attack. (dodge based on speed) doesn't work if opponent chooses dodge
    //flee
    //retreat to shooting range - opponent may get a free hit if they chose a melee attack (dodge based on speed) doesn't work if opponent chooses dodge
    //attack (melee or shoot)
    //counter (melee or shoot)
    //dodge / block in melee) (melee or shoot)

    //Actions cost 5/100 fight stam - whether you plan one two or three - must scale with toughness?? Don't want user to always win just cos they have a lot of stamina. Gambling must have a benefit.
    //maybe toughness just reduces damage - especially gun damage - since opposed str indicates melee dmg.


    //counter beats dodge for half, dodge beats attack full dmg, attack beats counter (but both hit) Attack does twice counter damage.
    //fleeing always incurs a chance of a hit regardless of opponent action, if not in melee range will be a gunshot
    //changing distance give a free full hit to counter and a free half hit to attack. 
    //dodge v dodge no dmg, atack v attack both dmg, counter v counter no damage.

    //action resolution:
    //determine if hits land using reflexes. 
    //determine damage using strength for melee, and weapon damage for shooting. 
    //attempts to shoot at melee range and melee at shooting range always fail

    //smart AI (I hope!)
    //AI will first try and go the distance best suited to it's actions - based on high str or high gun damage 
    //If AI is unsuccessful at changing range it will wait 0-2 turns before trying again
    //At it's best range it will choose an action: it will count how many attacks / dodge / counters PC used and weight it's choice accordingly, if you always counter it will attack, if you always attack it will dodge if you always dodge it will counter
    //AI may change range if after three or more rounds at a range 
    //If player tries a combo, AI may spot it after the first move
    //AI may have variants, random, smart (as described), cheat (determines best way to beat you, may be able to automatcially counter stacked actions.)
    

    //if run out of bullets no shooting options presented. After six shots, only choice is dodge(but no counter), flee, close to melee or reload. Reload gives free attack.  (or 2 for shotgun? rifle not usable in this type of encounter.)

    //class benefits - don't want to make shooter & melee overwhelmingly advantageous...
    //shooter can dodge and reload. 
    //melee can close to melee (once) without taking a free shot.
    //melee has higher strength & tough
    //shooter had better speed and reflexes.
    //melee damage affected by relative strengths, gun damage fixed. 

    //encounter consequences - take cosmetic damage win or lose based on result - losing always worse than winning
    //if lose: lose money - players take more than robots.
    //chance of real damage? Probably not...
    //if win: get money, maybe inventory (no useless looting tho), maybe something special (rare drop) like a clue to the bosses, or a quest lead to get more money, or better parts?
    //rewards commensurate with difficulty - matrix of random/smart/cheat and weak/medium/strong - weak random opps give least, cheating tough opponet most.

    //DEV plan:
    //flee
    //opposed rolls for attacks
    //change range success determination
    //AI change range inital
    //smart AI determines shotoing or brawling
    //cheat AI determines shooting or brawling
    //cosmetic damage tracker
    //rewards based on difficulty
    //ammo count / reload
    //npc descriptions give hint as to AI type and relative strength (don't want to be too predictable maybe?)
    //AI track user actions
    //player combos
   
    
    //Other considerations, should fights get harder based on game state - e.g. multiple encounters in one journey player has less and less stamina. NPCs are more likely to be tougher / smarter based on where you are in the main game.

    return (

        <div  className='encounter-container'>
            {/*determine options based on combat state */}           
            {!combatState.Rounds.length ? 
            <div>
                {gameState.PlayerCharacter!.Name}, you have encountered a {combatState.AI.Description}!                
            </div> : 

            <div>
                {gameState.PlayerCharacter!.Name}, you are fighting a {combatState.AI.Description}!
                <p/>    
                {combatState.Rounds.map(x => <p>{describeRound(x)}</p>) /* show result of last round */}                
            </div>}
            <p/> 
            <div>
                {combatState.AtMeleeRange ? "You are close enough to try and punch them!" : "You are a quite a ways away, best to try and shoot them."}               
            </div>
            <p/>    
            {getPlayerState(combatState)}, and the {combatState.AI.Description} {getAIState(combatState)}.
            <p/>    
            What will you do next?
            <p/>    
            {getPlayerOptions(combatState).map(x => <button  onClick={() => setCombatState(doAnAction(combatState, x))} >{CapitaliseWords(x)}</button>)}
            <p/>    
            <button onClick={() => updateGameState({ ...gameState, Phase: 'travelling', PlayerCharacter: { ...gameState.PlayerCharacter!, TravelTimeLeft: (gameState.PlayerCharacter?.TravelTimeLeft ?? 0) - 1 } })}>
                Start Travelling
            </button>
        </div>
    )
}


function containsAny(arg0: ECombatActions[]) {
    throw new Error("Function not implemented.");
}

